import { Text } from '@viapeople/components'
import { useEffect, useState } from 'react'
import { useFormBuilderStore } from '../store'
import { DynamicPropertyEditor } from './propertyEditors/DynamicPropertyEditor'

export function PropertyPanel() {
	const selectedComponentId = useFormBuilderStore((state) => state.selectedComponentId)
	const findComponentById = useFormBuilderStore((state) => state.findComponentById)
	const updateComponentProps = useFormBuilderStore((state) => state.updateComponentProps)
	const getComponentDefinition = useFormBuilderStore((state) => state.getComponentDefinition)

	const [selectedComponent, setSelectedComponent] = useState<ReturnType<typeof findComponentById>>(null)
	const [componentDefinition, setComponentDefinition] = useState<ReturnType<typeof getComponentDefinition>>(undefined)

	useEffect(() => {
		if (selectedComponentId) {
			const component = findComponentById(selectedComponentId)
			setSelectedComponent(component)
			if (component) {
				const { isSection } = component.custom as { isSection: boolean }
				const definition = getComponentDefinition(component.name, isSection)
				setComponentDefinition(definition)
			} else {
				// Component not found, clear state
				setSelectedComponent(null)
				setComponentDefinition(undefined)
			}
		} else {
			setSelectedComponent(null)
			setComponentDefinition(undefined)
		}
	}, [selectedComponentId, findComponentById, getComponentDefinition])

	const handlePropertyChange = (_: unknown, value: unknown) => {
		if (selectedComponentId) {
			updateComponentProps(selectedComponentId, value as Record<string, unknown>)

			// Update local state to reflect changes immediately
			// This ensures the UI updates without waiting for the next render cycle
			const updatedComponent = findComponentById(selectedComponentId)
			if (updatedComponent) {
				setSelectedComponent(updatedComponent)
			}
		}
	}

	if (!selectedComponent || !componentDefinition || !componentDefinition.propertyDefinitions) {
		return (
			<div className='p-4 pt-8 flex flex-col items-center text-neutral-500 sticky top-0 overflow-y-auto h-full'>
				<Text variant='small'>Select an item to edit its properties</Text>
			</div>
		)
	}

	// Safety check: ensure selectedComponent matches componentDefinition to prevent race conditions
	if (selectedComponent.name !== componentDefinition.name) {
		return (
			<div className='p-4 pt-8 flex flex-col items-center text-neutral-500 sticky top-0 overflow-y-auto h-full'>
				<Text variant='small'>Loading properties...</Text>
			</div>
		)
	}

	return (
		<div className='p-4 sticky top-0 overflow-y-auto h-full'>
			<div className='mb-4'>
				<Text variant='small' weight='semibold' className='text-left text-neutral-500'>
					Properties
				</Text>
			</div>

			<div className='space-y-4'>
				<DynamicPropertyEditor
					key={selectedComponentId}
					propertyName='props'
					propertyValue={selectedComponent.custom as Record<string, unknown>}
					componentDefinition={componentDefinition}
					onChange={handlePropertyChange}
				/>
			</div>
		</div>
	)
}
